<script setup lang="ts">
import { computed } from 'vue';
import CascadeSelect from 'primevue/cascadeselect';
import { useI18n } from 'vue-i18n';

import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';

const props = defineProps<{
  modelValue: OperatingCostInvoiceSimple | null; // v-model binding
  importSourceInvoices: OperatingCostInvoiceSimple[];
  currentContractId?: string | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: OperatingCostInvoiceSimple | null): void;
  (e: 'change', value: OperatingCostInvoiceSimple | null): void;
}>();

const { t, locale } = useI18n();

const selectedImportInvoice = computed({
  get: () => props.modelValue,
  set: (val) => {
    emit('update:modelValue', val);
    emit('change', val);
  },
});

// Helper for formatted period string
function formatInvoicePeriod(inv: OperatingCostInvoiceSimple): string {
  const start = inv.periodStart ? new Date(inv.periodStart) : null;
  const end = inv.periodEnd ? new Date(inv.periodEnd) : null;

  if (!start && !end) return '';

  const fmt = new Intl.DateTimeFormat(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });

  if (start && end) {
    return `${fmt.format(start)} → ${fmt.format(end)}`;
  }

  if (start) return fmt.format(start);
  return fmt.format(end!);
}

// Helper for nice invoice label
function buildInvoiceLabel(inv: OperatingCostInvoiceSimple): string {
  const base =
    inv.issuerLegalName || inv.contractName || inv.pointOfSaleName || inv.id || t('labels.unknown');

  const period = formatInvoicePeriod(inv);

  return period ? `${base} (${period})` : base;
}

// Cascade options: Contract group → Invoices
const importCascadeOptions = computed(() => {
  const list = props.importSourceInvoices ?? [];
  if (!list.length) return [];

  const groupsMap = new Map<string, { label: string; contractId: string | null; items: any[] }>();

  for (const inv of list) {
    const contractId = inv.contractId ?? '__none__';
    const contractLabel =
      inv.contractName || inv.contractId || t('pages.operatingcostinvoices.unknownContract');

    if (!groupsMap.has(contractId)) {
      groupsMap.set(contractId, {
        label: contractLabel,
        contractId: inv.contractId ?? null,
        items: [],
      });
    }

    const group = groupsMap.get(contractId)!;

    group.items.push({
      label: buildInvoiceLabel(inv),
      value: inv, // actual invoice
    });
  }

  // sort items inside groups by periodStart/issueDate DESC
  for (const group of groupsMap.values()) {
    group.items.sort((a, b) => {
      const invA = a.value as OperatingCostInvoiceSimple;
      const invB = b.value as OperatingCostInvoiceSimple;

      const aDate =
        invA.periodStart || invA.issueDate
          ? Date.parse(String(invA.periodStart ?? invA.issueDate))
          : 0;
      const bDate =
        invB.periodStart || invB.issueDate
          ? Date.parse(String(invB.periodStart ?? invB.issueDate))
          : 0;

      return bDate - aDate; // newest first
    });
  }

  // sort groups: current contract first, then alphabetically
  const current = props.currentContractId ?? null;
  const groups = Array.from(groupsMap.values());

  groups.sort((a, b) => {
    const aIsCurrent = a.contractId && a.contractId === current;
    const bIsCurrent = b.contractId && b.contractId === current;

    if (aIsCurrent && !bIsCurrent) return -1;
    if (!aIsCurrent && bIsCurrent) return 1;

    return a.label.localeCompare(b.label);
  });

  return groups;
});
</script>

<template>
  <CascadeSelect
    v-if="importCascadeOptions.length"
    v-model="selectedImportInvoice"
    :options="importCascadeOptions"
    optionLabel="label"
    optionGroupLabel="label"
    optionGroupChildren="items"
    optionValue="value"
    class="w-80"
    :disabled="disabled"
    :placeholder="t('pages.operatingcostinvoices.selectImportSource')"
  >
    <!-- Option template (dropdown list) -->
    <template #option="slotProps">
      <div class="flex flex-col">
        <!-- Leaf entries (invoices) -->
        <template v-if="slotProps.option.value">
          <span class="font-semibold">
            {{
              slotProps.option.value.issuerLegalName ||
              slotProps.option.value.contractName ||
              slotProps.option.value.pointOfSaleName ||
              slotProps.option.value.id
            }}
          </span>
          <span class="text-sm text-gray-500">
            {{ formatInvoicePeriod(slotProps.option.value) }}
          </span>
        </template>

        <!-- Groups (contract level) -->
        <template v-else>
          <span class="font-semibold">
            {{ slotProps.option.label }}
          </span>
        </template>
      </div>
    </template>

    <!-- Selected value template (only name, no period) -->
    <template #value="{ value }">
      <span v-if="value" class="font-semibold">
        {{ value.issuerLegalName || value.contractName || value.pointOfSaleName || value.id }}
      </span>
      <span v-else class="text-gray-400">
        {{ t('pages.operatingcostinvoices.selectImportSource') }}
      </span>
    </template>
  </CascadeSelect>
</template>
